(() => {
  const namespace = 'SakuraPdpContentsExplorer';
  if (window[namespace]) {
    window[namespace].initAll();
    return;
  }

  const instances = new WeakMap();

  function normalizedRouteRoot() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root === '/' ? '' : `/${root.replace(/^\/+|\/+$/g, '')}`;
  }

  function validatedPageUrl(rawUrl, { requireMarketRoot = false } = {}) {
    let url;
    try {
      url = new URL(rawUrl, location.origin);
    } catch (error) {
      return null;
    }
    if (url.origin !== location.origin) return null;

    const normalizedRoot = normalizedRouteRoot();
    const allowedPrefixes = requireMarketRoot && normalizedRoot
      ? [`${normalizedRoot}/pages/`]
      : ['/pages/'];
    if (normalizedRoot && !requireMarketRoot) allowedPrefixes.push(`${normalizedRoot}/pages/`);
    const allowed = allowedPrefixes.some((prefix) => {
      if (!url.pathname.startsWith(prefix)) return false;
      const encodedHandle = url.pathname.slice(prefix.length).replace(/\/$/, '');
      let handle;
      try {
        handle = decodeURIComponent(encodedHandle);
      } catch (error) {
        return false;
      }
      if (handle !== encodedHandle) return false;
      return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle);
    });
    return allowed ? url : null;
  }

  function localizedUrl(rawUrl) {
    const url = validatedPageUrl(rawUrl);
    if (!url) throw new Error('Unsupported product information URL');
    const normalizedRoot = normalizedRouteRoot();

    if (url.origin === location.origin && normalizedRoot && url.pathname.startsWith('/pages/')) {
      url.pathname = `${normalizedRoot}${url.pathname}`;
    }

    if (!validatedPageUrl(url.toString())) throw new Error('Unsupported localized product information URL');

    const previewTheme = new URLSearchParams(location.search).get('preview_theme_id');
    if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
    return url.toString();
  }

  function imageSource(image, width = 720) {
    const raw = image.getAttribute('src') || '';
    if (!raw) return '';
    const url = new URL(raw, location.origin);
    if (url.hostname.endsWith('shopify.com') || url.hostname === location.hostname) {
      url.searchParams.set('width', String(width));
    }
    return url.toString();
  }

  function sanitizeRichText(html) {
    const template = document.createElement('template');
    template.innerHTML = html;
    template.content.querySelectorAll('script, style, iframe, object, embed, form, input, button').forEach((node) => node.remove());
    template.content.querySelectorAll('a').forEach((link) => link.replaceWith(...link.childNodes));
    template.content.querySelectorAll('*').forEach((node) => {
      [...node.attributes].forEach((attribute) => {
        const keep = ['colspan', 'rowspan', 'scope'].includes(attribute.name);
        if (!keep) node.removeAttribute(attribute.name);
      });
    });
    return template.content.cloneNode(true);
  }

  async function fetchDocument(rawUrl, signal) {
    const requestUrl = localizedUrl(rawUrl);
    const response = await fetch(requestUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      redirect: 'error',
      signal,
    });
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    if (!validatedPageUrl(response.url, { requireMarketRoot: true })) {
      throw new Error('Unsupported product information response URL');
    }
    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function sourceGrid(documentFragment) {
    const candidates = [...documentFragment.querySelectorAll('[data-ui-component="Image With Text Columns Section"]')];
    return candidates.sort((left, right) => {
      const leftScore = left.querySelectorAll('a[href*="/pages/"]').length + left.querySelectorAll('img').length;
      const rightScore = right.querySelectorAll('a[href*="/pages/"]').length + right.querySelectorAll('img').length;
      return rightScore - leftScore;
    })[0];
  }

  function sourceItems(documentFragment) {
    const currentItems = embeddedItems(documentFragment);
    if (currentItems.length) return currentItems;

    const grid = sourceGrid(documentFragment);
    if (!grid) return [];

    const seen = new Set();
    return [...grid.querySelectorAll('.clm')].reduce((items, card) => {
      const links = [...card.querySelectorAll('a[href*="/pages/"]')];
      const link = links.find((item) => item.getAttribute('href'));
      const image = card.querySelector('img');
      if (!link || !image) return items;

      const rawUrl = link.getAttribute('href');
      if (!validatedPageUrl(rawUrl)) return items;
      const absoluteUrl = localizedUrl(rawUrl);
      const key = new URL(absoluteUrl).pathname;
      if (seen.has(key)) return items;

      const title = links.map((item) => item.textContent.trim()).find(Boolean)
        || image.getAttribute('alt')?.replace(/[_-]+/g, ' ').replace(/\.[a-z0-9]+$/i, '').trim();
      const src = imageSource(image);
      if (!title || !src) return items;

      seen.add(key);
      items.push({ title, url: absoluteUrl, src });
      return items;
    }, []);
  }

  function embeddedItems(section) {
    const manifest = section.querySelector('[data-contents-manifest]');
    if (!manifest) return [];

    try {
      const parsed = JSON.parse(manifest.textContent);
      if (!Array.isArray(parsed)) return [];
      const seen = new Set();
      return parsed.reduce((items, item) => {
        const title = String(item.title || '').trim();
        const rawUrl = String(item.url || '').trim();
        if (!title || !rawUrl) return items;
        if (!validatedPageUrl(rawUrl)) return items;
        const url = localizedUrl(rawUrl);
        const key = new URL(url).pathname;
        if (seen.has(key)) return items;
        seen.add(key);
        items.push({
          title,
          url,
          src: String(item.src || '').trim(),
          category: String(item.category || '').trim(),
        });
        return items;
      }, []);
    } catch (error) {
      return [];
    }
  }

  function sakuraDetailData(documentFragment, fallbackTitle, fallbackImage) {
    const detail = documentFragment.querySelector('[data-ui-component="Sakura Product Information Detail"]');
    if (!detail) return null;

    const title = detail.querySelector('.sakura-product-information-detail__hero h1')?.textContent.trim() || fallbackTitle;
    const description = detail.querySelector('.sakura-product-information-detail__description')?.innerHTML || '';
    const imageNodes = [
      ...detail.querySelectorAll('.sakura-product-information-detail__hero-media img'),
      ...detail.querySelectorAll('.sakura-product-information-detail__gallery img'),
    ];
    const seenImages = new Set();
    const images = imageNodes.reduce((result, image) => {
      const src = imageSource(image, 900);
      if (!src || seenImages.has(src)) return result;
      seenImages.add(src);
      result.push({ src, alt: image.getAttribute('alt') || title });
      return result;
    }, []).slice(0, 3);
    const factItems = [...detail.querySelectorAll('.sakura-product-information-detail__fact')].map((item) => ({
      heading: item.querySelector('summary span')?.textContent.trim()
        || item.querySelector('summary')?.textContent.trim()
        || '',
      body: item.querySelector('.sakura-product-information-detail__fact-content')?.innerHTML || '',
    })).filter((item) => item.heading && item.body);

    if (!title) throw new Error('No product information found');
    const resolvedImages = images.length || !fallbackImage
      ? images
      : [{ src: fallbackImage, alt: title }];
    return {
      title,
      description,
      images: resolvedImages,
      factItems,
      sourceIncomplete: !description && !factItems.length,
    };
  }

  function specializedDetailData(documentFragment, fallbackTitle, fallbackImage) {
    const warugaki = documentFragment.querySelector('.sakura-warugaki');
    if (warugaki) {
      const title = warugaki.querySelector('h1')?.textContent.trim() || fallbackTitle;
      const description = warugaki.querySelector('.sakura-warugaki__intro')?.innerHTML || '';
      const images = [...warugaki.querySelectorAll('.sakura-warugaki__gallery img')].map((image) => ({
        src: imageSource(image, 900),
        alt: image.getAttribute('alt') || title,
      })).filter((item) => item.src).slice(0, 3);
      const factItems = [];
      const preparation = warugaki.querySelector('.sakura-warugaki__preparation');
      const preparationBody = preparation?.querySelector('ol')?.outerHTML || '';
      if (preparationBody) {
        factItems.push({
          heading: preparation.querySelector('h2')?.textContent.trim() || 'Preparation',
          body: preparationBody,
        });
      }
      [...warugaki.querySelectorAll('.sakura-warugaki__faq details')].forEach((item) => {
        const heading = item.querySelector('summary')?.textContent.replace(/\+\s*$/, '').trim() || '';
        const body = item.querySelector('.sakura-warugaki__answer')?.innerHTML || '';
        if (heading && body) factItems.push({ heading, body });
      });
      return {
        title,
        description,
        images: images.length || !fallbackImage ? images : [{ src: fallbackImage, alt: title }],
        factItems,
        sourceIncomplete: !description && !factItems.length,
      };
    }

    const substitutes = documentFragment.querySelector('.sakura-substitutes');
    if (substitutes) {
      const title = substitutes.querySelector('h1')?.textContent.trim() || fallbackTitle;
      const description = substitutes.querySelector('.sakura-substitutes__intro')?.innerHTML || '';
      return {
        title,
        description,
        images: fallbackImage ? [{ src: fallbackImage, alt: title }] : [],
        factItems: [],
        sourceIncomplete: !description,
      };
    }

    return null;
  }

  function detailData(documentFragment, fallbackTitle, fallbackImage) {
    const currentDetail = sakuraDetailData(documentFragment, fallbackTitle, fallbackImage);
    if (currentDetail) return currentDetail;
    const specializedDetail = specializedDetailData(documentFragment, fallbackTitle, fallbackImage);
    if (specializedDetail) return specializedDetail;

    const intro = [...documentFragment.querySelectorAll('[data-ui-component="Text Section"]')]
      .find((section) => section.querySelector('.s-header__title') && section.querySelector('.s-header__desc'));
    const gallery = [...documentFragment.querySelectorAll('[data-ui-component="Image With Text Columns Section"]')]
      .find((section) => section.querySelector('img'));
    const facts = [...documentFragment.querySelectorAll('[data-ui-component="Collapsible Content Section"]')]
      .find((section) => section.querySelector('.accordion__item'));

    const title = intro?.querySelector('.s-header__title')?.textContent.trim() || fallbackTitle;
    const description = intro?.querySelector('.s-header__desc')?.innerHTML || '';
    const seenImages = new Set();
    const images = [...(gallery?.querySelectorAll('img') || [])].reduce((result, image) => {
      const src = imageSource(image, 900);
      if (!src || seenImages.has(src)) return result;
      seenImages.add(src);
      result.push({ src, alt: image.getAttribute('alt') || title });
      return result;
    }, []).slice(0, 3);
    const factItems = [...(facts?.querySelectorAll('.accordion__item') || [])].map((item) => ({
      heading: item.querySelector('.js-accordion-trigger')?.textContent.trim() || '',
      body: item.querySelector('.accordion__content')?.innerHTML || '',
    })).filter((item) => item.heading && item.body);

    if (!title) {
      throw new Error('No product information found');
    }

    if (!description && !images.length && !factItems.length) {
      const mainPage = documentFragment.querySelector('[data-ui-component="Main Page Section"]');
      if (!mainPage) throw new Error('No product information found');
      const fallbackImages = fallbackImage ? [{ src: fallbackImage, alt: title }] : [];
      return { title, description: '', images: fallbackImages, factItems: [], sourceIncomplete: true };
    }

    return { title, description, images, factItems, sourceIncomplete: false };
  }

  function buildCard(item, onSelect) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'sakura-pdp-contents__card';
    button.setAttribute('aria-label', `View ${item.title} information`);

    const media = document.createElement('span');
    media.className = 'sakura-pdp-contents__card-media';
    if (item.src) {
      const image = document.createElement('img');
      image.src = item.src;
      image.alt = item.title;
      image.loading = 'lazy';
      image.decoding = 'async';
      media.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'sakura-pdp-contents__card-placeholder';
      placeholder.textContent = item.title;
      media.append(placeholder);
    }

    const title = document.createElement('span');
    title.className = 'sakura-pdp-contents__card-title';
    title.textContent = item.title;
    button.append(media, title);
    button.addEventListener('click', () => onSelect(item, button));
    return button;
  }

  function renderGrid(grid, items, onSelect, headingTag) {
    const categories = [];
    const grouped = new Map();
    items.forEach((item) => {
      const category = item.category || '';
      if (!grouped.has(category)) {
        grouped.set(category, []);
        categories.push(category);
      }
      grouped.get(category).push(item);
    });

    const hasCategories = categories.some(Boolean);
    grid.classList.toggle('sakura-pdp-contents__grid--grouped', hasCategories);
    const fragment = document.createDocumentFragment();
    if (!hasCategories) {
      items.forEach((item) => fragment.append(buildCard(item, onSelect)));
      grid.replaceChildren(fragment);
      return;
    }

    categories.forEach((category) => {
      const group = document.createElement('section');
      group.className = 'sakura-pdp-contents__category';
      if (category) {
        const heading = document.createElement(headingTag);
        heading.textContent = category;
        group.append(heading);
      }
      const cards = document.createElement('div');
      cards.className = 'sakura-pdp-contents__category-grid';
      grouped.get(category).forEach((item) => cards.append(buildCard(item, onSelect)));
      group.append(cards);
      fragment.append(group);
    });
    grid.replaceChildren(fragment);
  }

  function renderDetail(host, data, headingTag, detailPanel) {
    host.replaceChildren();
    const layout = document.createElement('div');
    layout.className = 'sakura-pdp-contents__detail-layout';

    const gallery = document.createElement('div');
    gallery.className = 'sakura-pdp-contents__detail-gallery';
    data.images.forEach((item) => {
      const image = document.createElement('img');
      image.src = item.src;
      image.alt = item.alt;
      image.loading = 'lazy';
      image.decoding = 'async';
      gallery.append(image);
    });

    const body = document.createElement('div');
    const heading = document.createElement(headingTag);
    heading.tabIndex = -1;
    heading.textContent = data.title;
    body.append(heading);

    if (data.description) {
      const copy = document.createElement('div');
      copy.className = 'sakura-pdp-contents__detail-copy';
      copy.append(sanitizeRichText(data.description));
      body.append(copy);
    }

    if (data.factItems.length) {
      const facts = document.createElement('div');
      facts.className = 'sakura-pdp-contents__facts';
      data.factItems.forEach((item, index) => {
        const details = document.createElement('details');
        if (index === 0) details.open = true;
        const summary = document.createElement('summary');
        summary.textContent = item.heading;
        const content = document.createElement('div');
        content.className = 'sakura-pdp-contents__fact-body';
        content.append(sanitizeRichText(item.body));
        details.append(summary, content);
        facts.append(details);
      });
      body.append(facts);
    }

    if (data.images.length) layout.append(gallery);
    layout.append(body);
    host.append(layout);
    if (!detailPanel.hidden) heading.focus({ preventScroll: true });
  }

  function init(section) {
    if (instances.has(section)) return;
    const sourceController = new AbortController();

    const sourceUrl = section.dataset.sourceUrl;
    const headingTag = ['h2', 'h3'].includes(section.dataset.childHeadingTag)
      ? section.dataset.childHeadingTag
      : 'h3';
    const tabs = [...section.querySelectorAll('[data-contents-tab]')];
    const panels = [...section.querySelectorAll('[data-contents-panel]')];
    const overviewTab = section.querySelector('[data-contents-tab="overview"]');
    const detailTab = section.querySelector('[data-contents-tab="detail"]');
    const status = section.querySelector('[data-contents-status]');
    const grid = section.querySelector('[data-contents-grid]');
    const detail = section.querySelector('[data-contents-detail]');
    const back = section.querySelector('[data-contents-back]');
    const cache = new Map();
    let selectedCard = null;
    let detailController = null;
    let selectionId = 0;

    instances.set(section, {
      destroy() {
        sourceController.abort();
        detailController?.abort();
        selectionId += 1;
      },
    });

    function selectTab(name, focus = false) {
      if (name === 'overview' && detailController) {
        detailController.abort();
        detailController = null;
        selectionId += 1;
        detail.replaceChildren();
        detailTab.disabled = true;
        detailTab.textContent = 'Snack information';
      }
      tabs.forEach((tab) => {
        const active = tab.dataset.contentsTab === name;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active && focus) tab.focus();
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.contentsPanel !== name;
      });
    }

    async function selectItem(item, card) {
      selectionId += 1;
      const requestId = selectionId;
      detailController?.abort();
      detailController = new AbortController();
      selectedCard = card;
      detailTab.disabled = false;
      detailTab.textContent = item.title;
      detail.replaceChildren();
      const loading = document.createElement('p');
      loading.className = 'sakura-pdp-contents__status';
      loading.setAttribute('role', 'status');
      loading.textContent = `Loading ${item.title} information…`;
      detail.append(loading);
      selectTab('detail', true);

      try {
        let data = cache.get(item.url);
        if (!data) {
          data = detailData(await fetchDocument(item.url, detailController.signal), item.title, item.src);
          if (requestId !== selectionId) return;
          cache.set(item.url, data);
        }
        if (requestId !== selectionId) return;
        renderDetail(detail, data, headingTag, panels.find((panel) => panel.dataset.contentsPanel === 'detail'));
        detailController = null;
      } catch (error) {
        if (error.name === 'AbortError' || requestId !== selectionId) return;
        loading.textContent = 'Product information is temporarily unavailable. Please try another snack.';
      }
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        if (!tab.disabled) selectTab(tab.dataset.contentsTab);
      });
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const enabled = tabs.filter((item) => !item.disabled);
        let index = enabled.indexOf(tab);
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = enabled.length - 1;
        else index = (index + (event.key === 'ArrowRight' ? 1 : -1) + enabled.length) % enabled.length;
        event.preventDefault();
        selectTab(enabled[index].dataset.contentsTab, true);
      });
    });

    back.addEventListener('click', () => {
      selectTab('overview');
      selectedCard?.focus();
    });

    const localItems = embeddedItems(section);
    if (localItems.length) {
      renderGrid(grid, localItems, selectItem, headingTag);
      status.hidden = true;
    } else if (sourceUrl) {
      fetchDocument(sourceUrl, sourceController.signal)
        .then((documentFragment) => sourceItems(documentFragment))
        .then((items) => {
          if (!items.length) throw new Error('No assortment items found');
          renderGrid(grid, items, selectItem, headingTag);
          status.hidden = true;
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          status.textContent = 'The current assortment is temporarily unavailable.';
        });
    } else {
      status.textContent = 'The current assortment is temporarily unavailable.';
    }
  }

  function explorerNodes(root = document) {
    const selector = '[data-sakura-pdp-contents-explorer]';
    const descendants = [...root.querySelectorAll(selector)];
    return root.matches?.(selector) ? [root, ...descendants] : descendants;
  }

  function initAll(root = document) {
    explorerNodes(root).forEach(init);
  }

  function unload(root) {
    explorerNodes(root).forEach((section) => {
      instances.get(section)?.destroy();
      instances.delete(section);
    });
  }

  window[namespace] = { initAll, unload };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
})();
